import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import dayjs from "dayjs";
import Markdown from "markdown-to-jsx";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import flowMemorySingleton from "../../Services/FlowMemorySingleton";
import { ErrorOutline, InfoOutlined } from "@mui/icons-material";
import { s360ConfigService } from "../../Services/S360Config";
import { MessageType } from "./Message"; // Assuming MessageType is used elsewhere or intended for V1
import { Box, Button, Modal } from "@material-ui/core";
import {
  convertEpochToTimestamp,
  getCurrentTimeZone,
} from "../../../utils/dateConversion";
import Dompurify from "dompurify";
import "./TextMessage.css";
import apiService from "../../Services/APIService";

const allowedMessageStatus = ["SENT", "DELIVERED"];
export const TextMessage = React.memo(
  function TextMessage({
    text,
    richText,
    creator,
    senderName,
    messageStatus,
    messageId,
    onEditMessage,
    handleDeleteMessage,
    sentTimestamp,
    receivedTimestamp, // We will use this for the user message timestamp as requested
    senderId,
    enableEditingFunctionality = false,
    workitemId,
    isEdited,
    isDeleted,
    agentSandBoxInfo,
    downloadInProgress = false,
    messageSubType,
  }) {
    console.log(
      "[TextMessage] messageSubType:",
      messageSubType,
      "creator:",
      creator,
      "senderId:",
      senderId,
    );

    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(text);
    const [isHovered, setIsHovered] = useState(false);
    const [isIconHovered, setIsIconHovered] = useState(false);
    const [openDeleteMessageModal, setOpenDeleteMessageModal] = useState(false);
    const [timeZone, setTimeZone] = useState(getCurrentTimeZone());
    const [agentImage, setAgentImage] = useState(null);
    const [aiEmployeeName, setAiEmployeeName] = useState(null);

    const timeoutRef = useRef(null);
    const imageFetchedRef = useRef(false); // Track if image has been fetched for this message

    // --- Hover logic for showing icons ---
    const handleMouseEnter = () => {
      clearTimeout(timeoutRef.current);
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      if (!isIconHovered) {
        timeoutRef.current = setTimeout(() => {
          setIsHovered(false);
        }, 2000); // Set timeout to 2 seconds
      }
    };

    const handleIconMouseEnter = () => {
      clearTimeout(timeoutRef.current);
      setIsIconHovered(true);
    };

    const handleIconMouseLeave = () => {
      setIsIconHovered(false);

      // Only start timeout if the main message wrapper is not hovered
      // (Although mouse leave from icons usually means leaving the wrapper too,
      // this prevents edge cases if icons are very far)
      if (!isHovered) {
        // Re-check if the main wrapper is still hovered
        timeoutRef.current = setTimeout(() => {
          setIsHovered(false);
        }, 2000);
      }
    };
    // --- End Hover logic ---

    const handleEditStart = useCallback(() => {
      clearTimeout(timeoutRef.current); // Clear timeout when editing starts
      setIsEditing(true);
      setIsHovered(false); // Hide icons/timestamp on edit start
      setIsIconHovered(false); // Reset icon hover state
    }, []);

    const handleEditCancel = useCallback(() => {
      setIsEditing(false);
      setEditedText(text); // Revert to original text
    }, [text]);

    const handleEditSave = useCallback(() => {
      if (onEditMessage && editedText !== text) {
        onEditMessage(workitemId, messageId, editedText);
      }
      setIsEditing(false);
    }, [onEditMessage, workitemId, messageId, editedText, text]);

    const handleDelete = useCallback(() => {
      if (handleDeleteMessage) {
        handleDeleteMessage(workitemId, messageId);
      }
    }, [handleDeleteMessage, workitemId, messageId]);

    const senderNameStyle = {
      fontWeight: "bold",
      marginBottom: downloadInProgress ? "4px" : "0.25rem",
      fontSize: "x-small",
    };

    const configPrimaryFontColor =
      s360ConfigService.config?.configDetails?.primaryColor;
    const configprimaryColor =
      s360ConfigService.config?.configDetails?.primaryFontColor;

    const userMessageStyle = useMemo(
      () => ({
        position: "relative",
        padding: "12px 12px 5px",

        background: configPrimaryFontColor || "rgb(0, 95, 236)",
        color: configprimaryColor || "rgb(255, 255, 255)",
        maxWidth: "50vw",
        minWidth: "100px",
        wordBreak: "break-word",
        borderRadius: "8px",
        marginBottom: "1rem",
        marginTop: downloadInProgress ? "0px" : "1rem",
        fontWeight: "normal",
        animation: "0.3s ease-in-out 0s 1 normal none running fadeIn",
        boxShadow: downloadInProgress
          ? "none"
          : "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
        minHeight: "44px",
        border: "none",
        whiteSpace: "normal",
        wordWrap: "break-word",
      }),
      [configPrimaryFontColor, configprimaryColor],
    );

    const agentMessageStyle = useMemo(
      () => ({
        color: "#0B1029",
        marginBottom: "1rem",
        fontWeight: "normal",
        animation: "0.3s ease-in-out 0s 1 normal none running fadeIn",
        width: "auto",
        minHeight: "44px",
        padding: "0",
        gap: "0px",
        borderRadius: "8px",
        opacity: "1",
        angle: "0deg",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
      }),
      [],
    );

    const agentMessageTextArea = useMemo(
      () => ({
        backgroundColor: "rgb(247, 248, 250)",
        padding: "12px",
        borderRadius: "8px",
        color: "#0B1029",
        maxWidth: "60vw",
        height: "auto",
        overflowWrap: "break-word",
      }),
      [],
    );

    const detectURLs = useMemo(
      () => (text, color) => {
        if (typeof text !== "string") return text;
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text.split(urlRegex).map((part, index) =>
          urlRegex.test(part) ? (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: color }}
            >
              {part}
            </a>
          ) : (
            part
          ),
        );
      },
      [],
    );

    const renderMessageStatus = () => {
      if (s360ConfigService.config?.sandBox) {
        return (
          <div className="s360-message-status">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              style={{
                marginLeft: "5px",
                height: "16px",
                width: "16px",
                color: "rgb(74, 237, 14)",
              }}
              fill="currentColor"
            >
              <path d="M337 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-143 143L97 127c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l80 80c9.4 9.4 24.6 9.4 33.9 0L337 81zM441 201c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-247 247L41 295c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L143 465c9.4 9.4 24.6 9.4 33.9 0L441 201z" />
            </svg>
          </div>
        );
      }
      if ((creator === "USER" || creator === "CUSTOMER") && messageStatus) {
        switch (messageStatus) {
          case "SENT":
            return (
              <div className="s360-message-status">
                <CheckIcon
                  sx={{
                    marginLeft: "5px",
                    color: "white",
                    height: "16px",
                    width: "16px",
                  }}
                />
              </div>
            );
          case "DELIVERED":
            return (
              <div className="s360-message-status">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  style={{
                    marginLeft: "5px",
                    height: "16px",
                    width: "16px",
                    color: "white",
                  }}
                  fill="currentColor"
                >
                  <path d="M337 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-143 143L97 127c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l80 80c9.4 9.4 24.6 9.4 33.9 0L337 81zM441 201c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-247 247L41 295c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L143 465c9.4 9.4 24.6 9.4 33.9 0L441 201z" />
                </svg>
              </div>
            );

          case "READ":
            return (
              <div className="s360-message-status">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  style={{
                    marginLeft: "5px",
                    height: "16px",
                    width: "16px",
                    color: "rgb(74, 237, 14)",
                  }}
                  fill="currentColor"
                >
                  <path d="M337 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-143 143L97 127c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l80 80c9.4 9.4 24.6 9.4 33.9 0L337 81zM441 201c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-247 247L41 295c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9L143 465c9.4 9.4 24.6 9.4 33.9 0L441 201z" />
                </svg>
              </div>
            );
          default:
            return (
              <div className="s360-message-status">
                <ErrorOutline
                  sx={{
                    marginLeft: "5px",
                    color: "white",
                    height: "16px",
                    width: "16px",
                    color: "rgb(219,53,69,1)",
                  }}
                />
                <span className="failed-message-status">Failed to send</span>
              </div>
            );
        }
      }
      return null;
    };

    const parseNewlinesAndEscapes = useCallback((inputText) => {
      const A = ({ children, ...props }) => <a {...props}>{children}</a>;
      if (typeof inputText !== "string") {
        return inputText;
      }
      return (
        <Markdown
          children={text}
          options={{
            overrides: {
              a: {
                component: "A",
                props: {
                  style: {
                    color: ["AGENT", "BOT", "SYSTEM"].includes(creator)
                      ? s360ConfigService.config?.configDetails?.primaryColor
                      : s360ConfigService.config?.configDetails
                          ?.primaryFontColor,
                  },
                  target: "_blank",
                  rel: "noopener noreferrer",
                },
              },
            },
          }}
        />
      );
    }, []);

    useEffect(() => {
      return () => clearTimeout(timeoutRef.current);
    }, [agentSandBoxInfo]);
    useEffect(() => {}, [
      s360ConfigService.config.configDetails.showReadReceipt,
      s360ConfigService.config.configDetails.showAgentInfo,
    ]);

    const generateAgentAvatar = () => {
      if (s360ConfigService.config?.sandBox) {
        return (
          <svg
            width="30"
            height="30"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            style={{
              marginRight: "8px",
            }}
          >
            <circle
              cx="50"
              cy="50"
              r="48"
              stroke="#D1D5DB"
              strokeWidth="4"
              fill="#F9FAFB"
            />
            <circle cx="50" cy="35" r="15" fill="#9CA3AF" />
            <path
              d="M20 80c0-16.57 13.43-30 30-30s30 13.43 30 30"
              fill="#9CA3AF"
            />
          </svg>
        );
      }
      if (agentImage) {
        return (
          <img
            src={agentImage}
            alt=""
            style={{
              width: "30px",
              height: "30px",
              borderRadius: "20px",
              marginRight: "8px",
              objectFit: "cover",
            }}
          />
        );
      }
    };

    const fetchAIEmployeeImage = async (senderId) => {
      // Check cache first
      const cachedData = flowMemorySingleton.getAIEmployeeData(senderId);
      if (cachedData) {
        console.log(
          "[TextMessage] Using cached AI Employee data for:",
          senderId,
        );
        setAgentImage(cachedData.imageUrl);
        setAiEmployeeName(cachedData.name);
        return;
      }

      try {
        let aiEmployeeData = await apiService.getAIEmployeeDetails(senderId);
        const imageUrl = aiEmployeeData?.data?.imageURL;
        const name = aiEmployeeData?.data?.name;
        console.log("[TextMessage] AI Employee data:", { imageUrl, name });

        // Cache the data
        flowMemorySingleton.setAIEmployeeData(senderId, { imageUrl, name });

        setAgentImage(imageUrl);
        setAiEmployeeName(name);
        flowMemorySingleton.setCurrentAgentImageUrl(imageUrl);
      } catch (error) {
        console.error("[TextMessage] Error fetching AI Employee image:", error);
      }
    };

    const fetchNewAgentImage = async (senderId) => {
      let imgData = await apiService.getAgentImage(senderId);
      setAgentImage(imgData?.data?.signedURL);
      flowMemorySingleton.setCurrentAgentImageUrl(imgData?.data?.signedURL);
    };

    useEffect(() => {
      // Skip if already fetched for this message instance
      if (imageFetchedRef.current) {
        return;
      }

      if (messageSubType === "AIEMPLOYEE") {
        // For AI employees, check cache first
        const cachedData = flowMemorySingleton.getAIEmployeeData(senderId);
        if (cachedData) {
          setAgentImage(cachedData.imageUrl);
          setAiEmployeeName(cachedData.name);
        } else {
          fetchAIEmployeeImage(senderId);
        }
        imageFetchedRef.current = true;
      } else if (creator === "AGENT") {
        // For human agents, clear AI employee name and fetch agent image
        setAiEmployeeName(null);
        if (senderId !== flowMemorySingleton.getCurrentAgentUserId()) {
          flowMemorySingleton.setCurrentAgentUserId(senderId);
          fetchNewAgentImage(senderId);
        } else {
          setAgentImage(flowMemorySingleton.getCurrentAgentImageUrl());
        }
        imageFetchedRef.current = true;
      }
    }, [messageSubType, senderId, creator]);

    const handleCloseDeleteMessageModal = () => {
      setOpenDeleteMessageModal(false);
    };

    const onClickDelete = () => {
      setOpenDeleteMessageModal(false); // Close modal first
      handleDelete(); // Then call the delete handler
    };

    const isMessageEditable =
      !isEditing &&
      isHovered &&
      enableEditingFunctionality &&
      allowedMessageStatus.includes(messageStatus) &&
      flowMemorySingleton.getAgentMessageStarted() &&
      s360ConfigService.config.connectToNextiva;
    useEffect(() => {
      const checkTimezoneChange = () => {
        const current = getCurrentTimeZone();
        if (current !== timeZone) {
          setTimeZone(current);
        }
      };
      window.addEventListener("focus", checkTimezoneChange);
      return () => {
        window.removeEventListener("focus", checkTimezoneChange);
      };
    }, [timeZone]);

    const showAgentMessageInfo = () => {
      if (s360ConfigService.config?.sandBox) {
        return "JK - 3.08 pm";
      } else if (messageSubType === "AIEMPLOYEE" && aiEmployeeName) {
        return (
          <>
            {aiEmployeeName}{" "}
            {receivedTimestamp &&
              !downloadInProgress &&
              `- ${convertEpochToTimestamp(receivedTimestamp)}`}
          </>
        );
      } else {
        return (
          <>
            {senderName && senderName()}{" "}
            {receivedTimestamp &&
              !downloadInProgress &&
              `- ${convertEpochToTimestamp(receivedTimestamp)}`}
          </>
        );
      }
    };

    return (
      <>
        {/* Delete Message Modal */}
        <div className="simplify-modal">
          <Modal
            open={openDeleteMessageModal}
            onClose={handleCloseDeleteMessageModal}
            BackdropProps={{ invisible: true }}
            aria-labelledby="delete-message-modal-title"
            aria-describedby="delete-message-modal-description"
          >
            <Box
              sx={{
                top: "50%",
                left: "50%",
                width: "65%",
                maxWidth: "250px", // Constrain max width
                // height: "130px", // Let content determine height
                display: "flex",
                padding: "15px",
                position: "absolute",
                transform: "translate(-50%, -50%)",
                borderRadius: "5px",
                flexDirection: "column",
                justifyContent: "space-between",
                backgroundColor: "white",
                border: "1px solid",
              }}
            >
              <div
                id="delete-message-modal-title"
                style={{ fontWeight: "bold", fontSize: "14px" }}
              >
                Delete message for me
              </div>
              <div
                id="delete-message-modal-description"
                style={{ marginTop: "8px", fontSize: "10px", color: "grey" }}
              >
                This message will be deleted permanently.
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                  marginTop: "auto", // Push buttons to the bottom
                }}
              >
                <Button
                  variant="contained" // Use contained variant for primary action
                  color="default" // Or 'inherit' for minimal style
                  onClick={handleCloseDeleteMessageModal}
                  style={{
                    textTransform: "none",
                    boxShadow: "none", // Remove default button shadow if desired
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="secondary" // Use secondary for destructive action (often red/danger)
                  onClick={onClickDelete}
                  style={{
                    textTransform: "none",
                    boxShadow: "none",
                  }}
                >
                  Delete
                </Button>
              </div>
            </Box>
          </Modal>
        </div>

        {/* Main Message Container */}
        <div
          key={`${messageId}-parent`}
          id="main-text-container" // Consider if ID should be unique if multiple messages
          className="no-page-break"
        >
          {/* Agent/Bot Messages */}
          {creator === "AGENT" || creator === "BOT" || creator === "SYSTEM" ? (
            <>
              {" "}
              {/* Use fragment to wrap sender name and message */}
              {((senderName && creator === "AGENT") ||
                (messageSubType === "AIEMPLOYEE" && aiEmployeeName)) && (
                <div
                  className="sender-name-agent"
                  style={{
                    ...senderNameStyle,
                    paddingLeft:
                      !!agentImage &&
                      s360ConfigService?.config?.configDetails?.showAgentInfo
                        ? "40px"
                        : "0", // Add padding if avatar is present
                  }}
                  key={`${messageId}-sendername`}
                >
                  {showAgentMessageInfo()}
                </div>
              )}
              <div
                style={{
                  ...agentMessageStyle,
                  position: "relative",
                }}
                key={`${messageId}-${creator}-textparent`}
                className="no-page-break"
              >
                {creator &&
                  (creator === "AGENT" || messageSubType === "AIEMPLOYEE") &&
                  senderId &&
                  s360ConfigService?.config?.configDetails?.showAgentInfo &&
                  generateAgentAvatar()}{" "}
                {/* Render avatar */}
                <div style={agentMessageTextArea}>
                  {/* Render rich text or plain text */}
                  {richText !== "" && richText !== undefined ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: Dompurify.sanitize(richText, {
                          ADD_ATTR: ["target", "rel"],
                          FORCE_BODY: true,
                        }),
                      }}
                      key={`${messageId}-textrichtext`}
                      style={{
                        width: "100%",
                        wordWrap: "break-word",
                        display: "block",
                      }}
                    />
                  ) : (
                    <span key={`${messageId}-textspan`}>
                      {/* Apply detectURLs to plain text */}
                      {detectURLs(
                        parseNewlinesAndEscapes(text), // Parse escapes before detecting URLs
                        creator === "USER"
                          ? "#d6d6d6" // User link color (though this block is agent/bot)
                          : s360ConfigService.config?.configDetails
                              ?.primaryColor, // Agent/Bot link color
                      )}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : isDeleted ? ( // Deleted User Message
            <div
              key={`${messageId}-${creator}-textparent`}
              className="no-page-break"
              style={{
                padding: "10px 14px 13px 16px",
                borderRadius: "8px",
                border: "1px solid #d6d6d6", // Added border color for clarity
                maxWidth: "fit-content", // Prevent full width
                alignSelf: "flex-end", // Align right if parent is flex
                marginRight: "10px", // Add some margin
              }}
            >
              <InfoOutlined
                sx={{
                  position: "relative",
                  right: "6px",
                  top: "5px",
                  height: "18px",
                  width: "18px",
                  color: "#7A7D8C", // Added color
                }}
              />
              <span style={{ color: "#7A7D8C" }}>You deleted this message</span>{" "}
              {/* Added color */}
            </div>
          ) : (
            // Standard User Message
            <div className="message-container user-message-container">
              <div
                className="user-message-wrapper"
                style={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "flex-end", // Aligns children (the column and buttons/icons) to the right
                  width: "100%",
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {isMessageEditable && (
                  <div
                    className="edit-delete-icons-container"
                    style={{
                      position: "absolute",
                      right: "100%", // Position left of the message content
                      marginRight: "8px",
                      display: "flex",
                      alignItems: "center", // Vertically center icons
                      height: "100%", // Take full height of wrapper for centering
                    }}
                    onMouseEnter={handleIconMouseEnter}
                    onMouseLeave={handleIconMouseLeave}
                  >
                    {/* TIMESTAMP REMOVED FROM HERE */}
                    {/* ... icons ... */}
                    <div
                      className="icon-wrapper"
                      style={{ display: "flex", gap: "8px" }}
                    >
                      <svg
                        width="14"
                        height="15"
                        viewBox="0 0 14 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={() => setOpenDeleteMessageModal(true)}
                        style={{ cursor: "pointer" }}
                      >
                        <path
                          d="M5.52344 2.17188L5.00391 2.9375H8.96875L8.44922 2.17188C8.42188 2.11719 8.33984 2.0625 8.25781 2.0625H5.71484C5.63281 2.0625 5.55078 2.11719 5.52344 2.17188ZM9.54297 1.43359L10.5547 2.9375H10.9375H12.25H12.4688C12.8242 2.9375 13.125 3.23828 13.125 3.59375C13.125 3.97656 12.8242 4.25 12.4688 4.25H12.25V12.5625C12.25 13.793 11.2656 14.75 10.0625 14.75H3.9375C2.70703 14.75 1.75 13.793 1.75 12.5625V4.25H1.53125C1.14844 4.25 0.875 3.97656 0.875 3.59375C0.875 3.23828 1.14844 2.9375 1.53125 2.9375H1.75H3.0625H3.41797L4.42969 1.43359C4.70312 1.02344 5.19531 0.75 5.71484 0.75H8.25781C8.77734 0.75 9.26953 1.02344 9.54297 1.43359ZM3.0625 4.25V12.5625C3.0625 13.0547 3.44531 13.4375 3.9375 13.4375H10.0625C10.5273 13.4375 10.9375 13.0547 10.9375 12.5625V4.25H3.0625ZM5.25 6V11.6875C5.25 11.9336 5.03125 12.125 4.8125 12.125C4.56641 12.125 4.375 11.9336 4.375 11.6875V6C4.375 5.78125 4.56641 5.5625 4.8125 5.5625C5.03125 5.5625 5.25 5.78125 5.25 6ZM7.4375 6V11.6875C7.4375 11.9336 7.21875 12.125 7 12.125C6.75391 12.125 6.5625 11.9336 6.5625 11.6875V6C6.5625 5.78125 6.75391 5.5625 7 5.5625C7.21875 5.5625 7.4375 5.78125 7.4375 6ZM9.625 6V11.6875C9.625 11.9336 9.40625 12.125 9.1875 12.125C8.94141 12.125 8.75 11.9336 8.75 11.6875V6C8.75 5.78125 8.94141 5.5625 9.1875 5.5625C9.40625 5.5625 9.625 5.78125 9.625 6Z"
                          fill="#DB3545"
                        />
                      </svg>
                      <svg
                        onClick={handleEditStart}
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ cursor: "pointer" }}
                      >
                        <path
                          d="M12.0586 2.36328H12.0312C11.7852 2.11719 11.375 2.11719 11.1289 2.36328L10.3086 3.15625L11.5938 4.44141L12.3867 3.62109C12.6328 3.375 12.6328 2.96484 12.3867 2.69141L12.0586 2.36328ZM5.71484 7.77734C5.63281 7.83203 5.57812 7.94141 5.55078 8.05078L5.08594 9.63672L6.69922 9.19922C6.80859 9.17188 6.89062 9.11719 6.97266 9.03516L10.6641 5.34375L9.40625 4.08594L5.71484 7.77734ZM10.1992 1.43359C10.9648 0.667969 12.1953 0.667969 12.9609 1.43359L13.3164 1.78906C14.082 2.55469 14.082 3.78516 13.3164 4.55078L7.90234 9.96484C7.68359 10.1836 7.38281 10.3477 7.05469 10.457L4.32031 11.2227C4.10156 11.3047 3.85547 11.25 3.69141 11.0586C3.5 10.8945 3.44531 10.6484 3.5 10.4297L4.29297 7.69531C4.375 7.36719 4.56641 7.06641 4.78516 6.84766L10.1992 1.43359ZM2.40625 2.5H5.46875C5.82422 2.5 6.125 2.80078 6.125 3.15625C6.125 3.53906 5.82422 3.8125 5.46875 3.8125H2.40625C1.77734 3.8125 1.3125 4.30469 1.3125 4.90625V12.3438C1.3125 12.9727 1.77734 13.4375 2.40625 13.4375H9.84375C10.4453 13.4375 10.9375 12.9727 10.9375 12.3438V9.28125C10.9375 8.92578 11.2109 8.625 11.5938 8.625C11.9492 8.625 12.25 8.92578 12.25 9.28125V12.3438C12.25 13.6836 11.1562 14.75 9.84375 14.75H2.40625C1.06641 14.75 0 13.6836 0 12.3438L0 4.90625C0 3.59375 1.06641 2.5 2.40625 2.5Z"
                          fill="#7A7D8C"
                        />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Container for Message Bubble and Timestamp (stacked vertically) */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  {!isDeleted &&
                    !isEditing &&
                    !downloadInProgress &&
                    receivedTimestamp && (
                      <div
                        className="sender-name-agent"
                        style={{
                          ...senderNameStyle,
                          marginTop: "-2px",
                          whiteSpace: "nowrap",
                          textAlign: "right",
                          marginBottom: "-0.75rem",
                        }}
                      >
                        {convertEpochToTimestamp(receivedTimestamp)}
                      </div>
                    )}
                  <div className="user-message" style={userMessageStyle}>
                    {isEditing ? (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <textarea
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            style={{
                              backgroundColor: "transparent",
                              all: "unset", // Remove default textarea styles
                              width: "100%",
                              minHeight: "40px", // Set a reasonable minimum height
                              resize: "none", // Prevent manual resize
                              paddingRight: "30px", // Make space for status/edited label if needed (adjust as per layout)
                              color: "white", // Ensure text is visible on blue background
                            }}
                            autoFocus // Focus textarea on edit
                          />
                        </div>
                        {/* Edit/Save/Cancel buttons are positioned separately */}
                      </>
                    ) : (
                      <>
                        {" "}
                        {/* Display mode */}
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {/* Render text with parsed escapes and links */}
                          {detectURLs(
                            parseNewlinesAndEscapes(editedText), // Display editedText if saved
                            "#d6d6d6", // Link color for user messages
                          )}
                        </div>
                        {/* Status Icons & Edited Label (placed inside the bubble) */}
                        <div className="s360-message-receipt-container">
                          {isEdited && (
                            <div className="s360-message-edited-text-label">
                              Edited
                            </div>
                          )}
                          {s360ConfigService.config.configDetails
                            .showReadReceipt && renderMessageStatus()}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Edit/Save/Cancel Buttons (visible when editing) */}
                {isEditing && (
                  <div
                    className="edit-save-cancel-buttons"
                    style={{
                      position: "absolute",
                      right: "100%", // Position left of the message content
                      marginRight: "8px",
                      top: "50%",
                      transform: "translateY(-50%)", // Vertically center
                      display: "flex",
                      flexDirection: "column", // Stack buttons vertically
                      gap: "8px",
                    }}
                  >
                    <button
                      onClick={handleEditSave}
                      disabled={editedText === text || editedText.trim() === ""} // Disable if text is unchanged or empty
                      style={{
                        backgroundColor: "#005FEC",
                        width: "64px",
                        height: "38px",
                        borderRadius: "4px",
                        fontFamily: "Lato",
                        fontWeight: "800",
                        lineHeight: "16px",
                        color: "white",
                        border: "none",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        opacity:
                          editedText === text || editedText.trim() === ""
                            ? 0.6
                            : 1, // Indicate disabled state
                        pointerEvents:
                          editedText === text || editedText.trim() === ""
                            ? "none"
                            : "auto",
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={handleEditCancel}
                      style={{
                        backgroundColor: "white",
                        borderColor: "#005FEC",
                        width: "64px",
                        height: "38px",
                        borderRadius: "4px",
                        borderWidth: "2px",
                        borderStyle: "solid",
                        fontFamily: "Lato",
                        fontWeight: "800",
                        lineHeight: "16px",
                        color: "#005FEC",
                        cursor: "pointer",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Add receivedTimestamp to the comparison
    return (
      prevProps.messageStatus === nextProps.messageStatus &&
      prevProps.text === nextProps.text &&
      prevProps.richText === nextProps.richText &&
      prevProps.creator === nextProps.creator &&
      prevProps.messageId === nextProps.messageId &&
      prevProps.onEditMessage === nextProps.onEditMessage &&
      prevProps.handleDeleteMessage === nextProps.handleDeleteMessage &&
      prevProps.enableEditingFunctionality ===
        nextProps.enableEditingFunctionality &&
      prevProps.isEdited === nextProps.isEdited &&
      prevProps.isDeleted === nextProps.isDeleted &&
      prevProps.receivedTimestamp === nextProps.receivedTimestamp &&
      prevProps.sandBoxedReadreciept === nextProps.sandBoxedReadreciept &&
      prevProps.sandBoxedAgentInfo === nextProps.sandBoxedAgentInfo &&
      prevProps.messageSubType === nextProps.messageSubType &&
      prevProps.senderId === nextProps.senderId
    );
  },
);

// TextMessageV1 remains unchanged as per the request focus
export function TextMessageV1(props) {
  const isLink = (string) => {
    const urlPattern = /https?:\/\/[^\s/$.?#].[^\s]*/gi;
    return urlPattern.test(props?.message?.message?.text); // Use props.message.message.text
  };

  const detectURLs = (text, color) => {
    if (typeof text !== "string") return text;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) =>
      urlRegex.test(part) ? (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: color }}
        >
          {" "}
          {/* Added color style */}
          {part}
        </a>
      ) : (
        part
      ),
    );
  };

  // This function seems specific to handling href attributes, not general text/link detection
  const extractLinkFunction = (message) => {
    if (message.includes("href")) {
      const match = message.match(/"([^"]*)"/); // Find content within quotes
      if (match && match[1]) {
        // Check if match exists and captured group exists
        return match[1]; // Return the content inside the quotes
      }
    }
    return message; // Return original message if no href or no quotes found
  };

  const showMessageTime = () => {
    // This checks props.message.type, which is different from the first component's creator prop
    return props.message.type !== MessageType.info;
  };

  const messageTimeClassName = () => {
    switch (props.message.type) {
      case MessageType.sent:
        return "s360-message-time-sent";
      case MessageType.received:
        return "s360-message-time-received";
      case MessageType.info:
        return "s360-message-time-info";
      default:
        return "";
    }
  };

  const messageBoxClassName = () => {
    switch (props.message.type) {
      case MessageType.sent:
        return "s360-message-box-sent";
      case MessageType.received:
        return "s360-message-box-received";
      case MessageType.info:
        return props.convoType === "TRANSFER"
          ? "s360-message-box-info-agent" // Specific info box class for TRANSFER convo type
          : "s360-message-box-info"; // Generic info box class
      default:
        return "";
    }
  };

  const messageContainerClassName = () => {
    switch (props.message.type) {
      case MessageType.sent:
        return "s360-message-cotainer-sent";
      case MessageType.received:
        // Using a ternary based on a config flag for fullscreen mode
        return s360ConfigService.config.autoFullScreen
          ? "s360-message-cotainer-received-fs"
          : "s360-message-cotainer-received";
      case MessageType.info:
        return "s360-message-cotainer-info";
      default:
        return "";
    }
  };

  const messageBoxStyle = () => {
    let config = props.config;
    // Styles based on message type, potentially coming from a config object
    switch (props.message.type) {
      case MessageType.sent:
        return config?.customDesign?.textMessageBoxSender;
      case MessageType.received:
        return config?.customDesign?.textMessageBoxReceiver;
      case MessageType.info:
        return config?.customDesign?.textMessageBoxInfo;
      default:
        return {}; // Return empty object for unknown types
    }
  };

  const messageTimeStyle = () => {
    let config = props.config;
    // Styles based on message type for the timestamp text
    switch (props.message.type) {
      case MessageType.received:
        // Note: This seems like a potential bug, usually the timestamp color/style is different from the message box style
        return config?.customDesign?.textMessageBoxReceiver;

      case MessageType.sent:
      case MessageType.info:
        return config?.customDesign?.textMessageTime;
      default:
        return {};
    }
  };

  // Getting the message text prop
  const text = props?.message?.message?.text;
  // The original code had text?.replace(/\n/g, "<br/>"); here, but not assigned, so it did nothing.
  // We'll apply it inside the dangerouslySetInnerHTML div.

  return (
    <div className={messageContainerClassName()}>
      {/* Conditional rendering based on whether the message contains a link */}
      {isLink(props?.message?.message?.text) ? (
        <div className={messageBoxClassName()} style={messageBoxStyle()}>
          {/* Render text with detectURLs, color handled by anchor style inside detectURLs */}
          {detectURLs(props?.message?.message?.text)}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "end",
            justifyContent: "center", // Centering the single message box if no link
          }}
        >
          {/* Render plain text, replacing newlines with <br/> */}
          <div
            className={messageBoxClassName()}
            style={messageBoxStyle()}
            dangerouslySetInnerHTML={{
              __html: Dompurify.sanitize(text?.replace(/\n/g, "<br/>"), {
                ADD_ATTR: ["target", "rel"],
                FORCE_BODY: true,
              }),
            }}
          ></div>
        </div>
      )}
      {/* Timestamp Container */}
      <div style={{ display: "flex", alignItems: "center", marginRight: 7 }}>
        {" "}
        {/* Add some margin to the right */}
        {/* Show timestamp only if showMessageTime returns true */}
        {showMessageTime() && (
          <div className={messageTimeClassName()} style={messageTimeStyle()}>
            {/* Display formatted timestamp using dayjs */}
            {/* Using sentTimestamp as in the original V1 code */}
            {props?.message?.message?.sentTimestamp &&
              dayjs
                .unix(Math.trunc(props?.message?.message?.sentTimestamp / 1000))
                .format("DD MMM h:mm a")}
          </div>
        )}
        {/* V1 doesn't seem to handle read receipts explicitly in the provided code */}
      </div>
    </div>
  );
}
